import { useEffect, useRef, useState, useCallback } from 'react';
import { BrandStoreForMap, fetchNeighborhoodStoresForViewport } from '../../services/mapPageService';
import { LayerData } from '../../components/LayerCard/LayerCard';

interface ViewportNeighborhoodsState {
  viewportNeighborhoods: Record<string, BrandStoreForMap[]>;
  isFetchingNeighborhoods: boolean;
}

export const useViewportNeighborhoods = (
  map: google.maps.Map | null,
  layers: LayerData[],
  layerStores: Record<string, BrandStoreForMap[]>
): ViewportNeighborhoodsState => {
  const [viewportNeighborhoods, setViewportNeighborhoods] = useState<Record<string, BrandStoreForMap[]>>({});
  const [isFetchingNeighborhoods, setIsFetchingNeighborhoods] = useState(false);
  const previousZoomRef = useRef<number | null>(null);
  const previousPrimaryIdsRef = useRef<Record<string, string>>({});

  const isStoreInViewport = useCallback(
    (store: BrandStoreForMap, bounds: google.maps.LatLngBounds): boolean => {
      if (!store.latitude || !store.longitude) return false;
      const storeLatLng = new window.google.maps.LatLng(store.latitude, store.longitude);
      return bounds.contains(storeLatLng);
    },
    []
  );

  const fetchLayerNeighborhoods = useCallback(
    async (
      layer: LayerData,
      bounds: google.maps.LatLngBounds,
      primaryStores: BrandStoreForMap[]
    ): Promise<BrandStoreForMap[]> => {
      const primaryStoresInViewport = primaryStores.filter(store => isStoreInViewport(store, bounds));

      if (primaryStoresInViewport.length === 0) return [];

      const normalizedCustomerType = (layer.neighborhoodCustomerType === 'Non-customers'
        ? 'Non-Customers'
        : layer.neighborhoodCustomerType) || 'All';

      try {
        const stores = await fetchNeighborhoodStoresForViewport(
          primaryStoresInViewport,
          3,
          normalizedCustomerType,
          layer.neighborhoodChannels,
          layer.neighborhoodRetailers
        );
        return stores;
      } catch (error) {
        return [];
      }
    },
    [isStoreInViewport]
  );

  useEffect(() => {
    if (!map || !window.google) return;

    const handleMapIdle = async () => {
      const currentZoom = map.getZoom() ?? 4;
      const isDetailedZoom = currentZoom >= 13;

      if (!isDetailedZoom) {
        if (previousZoomRef.current !== null && previousZoomRef.current >= 13) {
          setViewportNeighborhoods({});
          previousPrimaryIdsRef.current = {};
        }
        previousZoomRef.current = currentZoom;
        return;
      }

      previousZoomRef.current = currentZoom;

      const bounds = map.getBounds();
      if (!bounds) return;

      const layersToFetch = layers.filter(layer => layer.visible && layer.showNeighborhoodStores);

      const changedLayers: LayerData[] = [];
      const currentPrimaryIds: Record<string, string> = {};

      for (const layer of layersToFetch) {
        const primaryStores = layerStores[layer.id] || [];
        const viewportStoreIds = primaryStores
          .filter(store => isStoreInViewport(store, bounds))
          .map(store => store.id)
          .sort()
          .join(',');

        currentPrimaryIds[layer.id] = viewportStoreIds;

        if (viewportStoreIds !== previousPrimaryIdsRef.current[layer.id]) {
          changedLayers.push(layer);
        }
      }

      if (changedLayers.length === 0) return;

      previousPrimaryIdsRef.current = currentPrimaryIds;

      setIsFetchingNeighborhoods(true);

      try {
        const neighborhoodResults: Record<string, BrandStoreForMap[]> = {};

        const fetchPromises = changedLayers.map(async layer => {
          const primaryStores = layerStores[layer.id] || [];
          const neighborhoods = await fetchLayerNeighborhoods(layer, bounds, primaryStores);
          return { layerId: layer.id, neighborhoods };
        });

        const results = await Promise.all(fetchPromises);

        results.forEach(({ layerId, neighborhoods }) => {
          neighborhoodResults[layerId] = neighborhoods;
        });

        setViewportNeighborhoods(neighborhoodResults);
      } catch (error) {
      } finally {
        setIsFetchingNeighborhoods(false);
      }
    };

    const idleListener = map.addListener('idle', handleMapIdle);
    handleMapIdle();

    return () => {
      idleListener.remove();
    };
  }, [map, layers, layerStores, fetchLayerNeighborhoods, isStoreInViewport]);

  useEffect(() => {
    const visibleLayersWithNeighborhoods = new Set(
      layers
        .filter(layer => layer.visible && layer.showNeighborhoodStores)
        .map(layer => layer.id)
    );

    setViewportNeighborhoods(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(layerId => {
        if (!visibleLayersWithNeighborhoods.has(layerId)) {
          delete updated[layerId];
        }
      });
      return updated;
    });

    previousPrimaryIdsRef.current = {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers.length]);

  return {
    viewportNeighborhoods,
    isFetchingNeighborhoods,
  };
};
